import type { Company } from '@/api';

export const useCompanyBusinessVerification = (
    company: Readonly<Ref<Company | undefined>>,
    state: Readonly<Ref<Pick<Company, 'business_identification' | 'cricos_number' | 'rto_number'>>>,
    loading?: Readonly<Ref<boolean | undefined>>,
) => {
    const abnStatus = computed(() =>
        match({
            status: company.value?.business_identification_verification_status,
            loading: loading?.value ?? false,
            active: company.value?.business_active,
        })
            .with({ loading: true }, () => 'loading' as const)
            .with({ status: 'verified', active: false }, () => 'error' as const)
            .with({ status: 'verified' }, () => 'success' as const)
            .with({ status: 'unverified' }, () => 'loading' as const)
            .with({ status: 'failed' }, () => 'error' as const)
            .otherwise(() => 'none' as const),
    );

    const statuses = computed(() =>
        match({
            status: company.value?.business_identification_verification_status,
            gst_start: company.value?.gst_registered_from,
            gst_end: company.value?.gst_registered_to,
            business_name: company.value?.business_name,
            legal_name: company.value?.legal_name,
        })
            .with({ status: 'verified' }, v => ({
                'GST Status':
                    v.gst_start == null
                        ? 'Unregistered'
                        : v.gst_end == null
                          ? `Registered since ${v.gst_start.toLocaleDateString()}`
                          : 'Expired',
                'Business Name': v.business_name ?? 'N/A',
                'Legal Name': v.legal_name ?? 'N/A',
            }))
            .otherwise(() => ({
                'GST Status': 'N/A',
                'Business Name': 'N/A',
                'Legal Name': 'N/A',
            })),
    );

    const isVerifying = computed(() => company.value?.business_identification_verification_status === 'unverified');

    const verifyMessage = computed((): string | undefined => {
        if (company.value == null) {
            return 'Fill new ABN.';
        }
        if (
            company.value.business_identification === state.value.business_identification &&
            (company.value.company_type !== 'school' ||
                (company.value.cricos_number === state.value.cricos_number &&
                    company.value.rto_number === state.value.rto_number))
        ) {
            return loading?.value || isVerifying.value ? 'Verifying...' : 'Already checked.';
        }

        return undefined;
    });

    return {
        abnStatus,
        isVerifying,
        statuses,
        verifyMessage,
    };
};
